<script>
  import { onMount, createEventDispatcher } from 'svelte';
	import InputContainer from './InputContainer.svelte';

  const dispatch = createEventDispatcher();
	
  export let label = "";
  export let placeholder = label;
  export let helpText = "";
  export let errorText = "";
  export let width = undefined;
  export let height = "32px";
  export let dropDownOnly = false;
  export let clearErrorOnEdit = false;
  export let items = [];
  export let displayMember = "name";
  export let selectedItem = undefined;
  export let text;
  
  export function focus() {
    inputElement.focus();
  }

	let inputId;
  let inputElement;
  let inputWidth;
  let arrowElement;
  let arrowHover = false;
  let dropDownVisible = false;
  let hoveredIndex = -1;
  let displayedItems = [];

  const arrowSize = 8;

  $: text = selectedItem ? selectedItem[displayMember] ? selectedItem[displayMember] : selectedItem : "";

  function showDropdown() {
    displayedItems = items;
    dropDownVisible = !dropDownVisible;
  }

  const noSearchKeys = [ "Escape", "ArrowUp", "ArrowDown", "Enter", "Tab", "Shift" ];
  function search(e) {
    if (clearErrorOnEdit) errorText = "";
    if (items.length && !noSearchKeys.includes(e.key)) {
      const count = displayedItems.length;
      if (text && text.length) displayedItems = items.filter(x => itemContains(x, text));
      else displayedItems = items;
      
      if (count !== displayedItems.length) hoveredIndex = -1;

      dropDownVisible = true;
    }
  }

  function itemContains(item, search) {
    let contains = false;
    if (item[displayMember]) contains = item[displayMember].toLowerCase().includes(search.toLowerCase());
    else contains |= item.toLowerCase().includes(search.toLowerCase());
    return contains;
  }

  function selectItem() {
    if (clearErrorOnEdit) errorText = "";
    selectedItem = displayedItems[hoveredIndex];
    dropDownVisible = false;
  }

  function handleWindowClick(e) {
    if (e.target !== inputElement && e.target !== arrowElement) {
      dropDownVisible = false;
    }
  }

  function handleWindowKeydown(e) {
    if (e.srcElement.id !== inputId) return;

    if (dropDownVisible) {
      if (e.key === "Escape") {
        dropDownVisible = false;
      }
      else if (e.key === "ArrowDown") {
        hoveredIndex = (hoveredIndex + 1) % displayedItems.length;
        if (e.target === inputElement) e.preventDefault();
      }
      else if (e.key === "ArrowUp") {
        const index = (hoveredIndex - 1) % displayedItems.length;
        hoveredIndex = index >= 0 ? index : displayedItems.length - 1;
        if (e.target === inputElement) e.preventDefault();
      }
      else if (e.key === "Enter")  {
        selectItem();
        if (e.target === inputElement) e.preventDefault();
      }
      else if (e.key === "Tab") {
        if (hoveredIndex >= 0) selectItem();
        dropDownVisible = false;
      }
      
      if (e.target !== inputElement) {
        e.preventDefault();
      }
    } else {
      if (e.key === "ArrowDown") {
        let index = displayedItems.indexOf(selectedItem);
        selectedItem = displayedItems[mod(index + 1, displayedItems.length)];
      }
      else if (e.key === "ArrowUp") {
        let index = displayedItems.indexOf(selectedItem);
        selectedItem = displayedItems[mod(index - 1, displayedItems.length)];
      }
    }
  }

  function mod(num, m) {
    return ((num % m) + m) % m;
  }

  function onChange() {
    dispatch('change', {text});
  }

  onMount(() => {
    displayedItems = items;
  });
</script>

<svelte:window on:click={(e) => handleWindowClick(e)} on:keydown={(e) => handleWindowKeydown(e)}/>
<InputContainer bind:inputId {label} {helpText} {errorText} {width}>
	<div>
    <div class="container" bind:clientWidth={inputWidth}>
			<input id={inputId} bind:this={inputElement} bind:value={text} readonly={dropDownOnly} {placeholder} class:arrowHover class:dropDownOnly class:dropDownVisible
				on:change={onChange} on:click={() => { if (dropDownOnly && !dropDownVisible) showDropdown() }} on:keyup={(e) => search(e)} style="height: {height}">
			{#if items.length}
				<div class="arrowContainer" bind:this={arrowElement} on:mouseenter={() => arrowHover = true} on:mouseleave={() => arrowHover = false} on:click={() => showDropdown()}>
					<span class="arrow" style="width: {arrowSize}px; height: {arrowSize}px;"/>
				</div>
			{/if}
		</div>
    <div class="dropDown" class:visible={dropDownVisible} style="width: {inputWidth}px">
      {#each displayedItems as item, i}
        <div class="item" class:hovered={i === hoveredIndex} on:mouseenter={() => hoveredIndex = i} on:click={() => selectItem()}>{item.name ? item.name : item}</div>
      {/each}
    </div>
  </div>
</InputContainer>
	
<style>
  .container {
    display: grid;
    grid-gap: 0;
    grid-template-columns: auto 30px;
    grid-template-rows: 1fr;
  }

  input {
    grid-row: 1;
    grid-column: 1 / span 2;
    border-radius: var(--border-radius);
    outline: none;
    resize: none;
    overflow: auto;
    pointer-events: auto;
    margin: 0;
  }

  input.dropDownOnly {
    cursor: default;
  }

  input:hover, input.arrowHover {
    border-color: gray;
  }

  input:focus, input.dropDownVisible {
    border-color: var(--primary-color);
  }

  .arrowContainer {
    display: flex;
    justify-content: center;
    align-items: center;
    grid-row: 1;
    grid-column: 2;
  }

  .arrowContainer:hover > .arrow {
    border-color: gray;
  }

  .arrow {
    position: absolute;
    border-style: solid;
    border-color: #aaa;
    border-width: 0 2px 2px 0;
    pointer-events: none;
    -webkit-transform: translateY(-2px) rotate(45deg);
    -ms-transform: translateY(-2px) rotate(45deg);
    transform: translateY(-2px) rotate(45deg);
  }

  .dropDown {
    display: none;
    position: absolute;
    background-color: #fff;
    max-height: 400px;
    border-radius: 2px;
    box-shadow: 0px 8px 8px 0px rgba(0,0,0,0.2);
    z-index: 1;
    overflow-y: auto;
  }

  .visible {
    display: block;
  }

  .item {
    display: block;
    text-align: left;
    color: black;
    padding: 6px 10px;
    height: 20px;
    cursor: default;
  }

  .item.hovered {
    background-color: #ddd;
  }

  ::-webkit-scrollbar {
    width: 16px;
  }

  ::-webkit-scrollbar-track {
    background: #f3f3f3;
  }

  ::-webkit-scrollbar-thumb {
    background: #ccc;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #888;
  }
</style>